import React, { useCallback, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../ui/accordion";
import { cn } from "../../lib/utils";
import TimelineSessionItem from "./TimelineSessionItem";

const TimelineWeek = React.forwardRef(
  (
    {
      week,
      isCurrent,
      isDeload,
      dateRange,
      nextSessionId,
      onSessionClick,
      onEditSession,
      onClearSession,
      onReorder,
      onPlanWeek,
    },
    ref,
  ) => {
    const sessions = useMemo(() => week.sessions || [], [week.sessions]);
    const completed = sessions.filter((s) => s.status === "completed").length;
    const total = sessions.length;
    const allDone = total > 0 && completed === total;

    // Small activation distance so a tap still registers as a tap, not a drag.
    const sensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      }),
    );

    const handleDragEnd = useCallback(
      (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const ids = sessions.map((s) => s.id);
        const oldIndex = ids.indexOf(active.id);
        const newIndex = ids.indexOf(over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        const reordered = arrayMove(ids, oldIndex, newIndex);
        onReorder?.(week.week_number, reordered);
      },
      [sessions, onReorder, week.week_number],
    );

    return (
      <AccordionItem
        value={`week-${week.week_number}`}
        ref={ref}
        className={cn(
          "overflow-hidden rounded-2xl border bg-card",
          isCurrent ? "border-primary/40" : "border-border",
        )}
      >
        <AccordionTrigger className="px-3 py-3 hover:no-underline">
          <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-1 pr-2 text-left">
            <span className="text-[15px] font-bold text-white">
              Week {week.week_number}
            </span>
            {dateRange && (
              <span className="text-xs font-normal text-muted-foreground">
                {dateRange}
              </span>
            )}
            {isCurrent && (
              <span className="rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-primary">
                This week
              </span>
            )}
            {isDeload && (
              <span className="rounded-md bg-yellow-500/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-yellow-500">
                Deload
              </span>
            )}
          </div>
          {total > 0 && (
            <span
              className={cn(
                "mr-2 text-xs font-normal tabular-nums",
                allDone ? "text-secondary" : "text-muted-foreground",
              )}
            >
              {completed}/{total}
            </span>
          )}
        </AccordionTrigger>

        <AccordionContent className="border-t border-border pb-0">
          {onPlanWeek && (
            <button
              type="button"
              onClick={onPlanWeek}
              className="flex w-full items-center justify-between gap-2 border-b border-border px-3 py-2.5 text-left text-sm font-semibold text-primary transition-colors hover:bg-muted/30"
            >
              Plan this week
              <span className="text-xs font-normal text-muted-foreground">Move, pool or skip sessions</span>
            </button>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sessions.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="divide-y divide-border">
                {sessions.map((session) => (
                  <TimelineSessionItem
                    key={session.id}
                    session={session}
                    isNext={session.id === nextSessionId}
                    onClickSession={() => onSessionClick(session)}
                    onEditSession={onEditSession}
                    onClearSession={onClearSession}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </AccordionContent>
      </AccordionItem>
    );
  },
);

TimelineWeek.displayName = "TimelineWeek";

export default TimelineWeek;
